import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SocietiesService, User, UserTypeEnum } from '@pwc-bei/common';
import { piAlert, PwcIconsLibrary } from '@pwc-bei/icons';
import {
  AuthenticationService,
  Authority,
  AuthorizationService,
} from '@pwc-bei/security';
import { EAccessPlanVariable } from 'src/app/enum/access-plan-variable';
import { RoutePath } from 'src/app/enums/route-path.enum';
import { AuthenticationUtilsService } from 'src/app/services/authentication-utils.service';
import { TermsAndConditionService } from 'src/app/services/terms-and-condition.service';
import { environment } from '../../../environments/environment';
import { ESharedDocumentType } from "../../enums/shared-document-type";
import { SharedDocumentUploadService } from "../../services/shared-document/shared-document-upload.service";

@Component({
    selector: 'app-terms-conditions',
    templateUrl: './terms-conditions.component.html',
    styleUrls: ['./terms-conditions.component.scss'],
    standalone: false
})
export class TermsConditionsComponent implements OnInit {
  constructor(
    private authenticationUtilsService: AuthenticationUtilsService,
    private termsAndConditionService: TermsAndConditionService,
    private formBuilder: FormBuilder,
    private router: Router,
    private authorizationService: AuthorizationService,
    private authenticationService: AuthenticationService,
    private sharedDocumentUploadService: SharedDocumentUploadService,
    private societyService: SocietiesService,
    private pwcIconsLibrary: PwcIconsLibrary
  ) { }

  user: User;
  termsAndConditionsForm: FormGroup;
  isLr: boolean = false;
  isUserSmart: boolean = false;

  showPage: boolean = false;

  buttonAcceptEnabled: boolean = false;

  generalConditionDownloaded: boolean = false;
  orderFormDownloaded: boolean = false;
  readonly directChannel = environment.directChannel;

  societyId: number;

  ngOnInit(): void {
    this.authenticationService.dataUser().subscribe((user) => {
      this.isLr = user.userType === UserTypeEnum.LEGAL_REPRESENTATIVE;
      if (!this.isLr) {
        this.orderFormDownloaded = true;
      }
    });

    this.authenticationService.dataUser().subscribe((user: any) => {
      this.user = user;
    });

    this.pwcIconsLibrary.registerIcons([
      piAlert
    ]);


    this.authorizationService
      .getAuthorities()
      .subscribe((auths: Authority[]) => {
        var authorities: string[] = auths.map(a => a.code)
        this.isUserSmart = authorities.filter(a => a.localeCompare('ROLE_BANDI_B2C_SMART') == 0).length > 0;
        var isUserPlus: boolean = authorities.filter(a => a.localeCompare('ROLE_BANDI_B2C_PLUS') == 0).length > 0;
        this.authenticationUtilsService.setSessionVariable('isUserSmart', this.isUserSmart);
        this.authenticationUtilsService.setSessionVariable('isUserPlus', isUserPlus);
        var isUserPremium = !(isUserPlus || this.isUserSmart);
        this.authenticationUtilsService.setSessionVariable('isUserPremium', isUserPremium);
        this.orderFormDownloaded = this.orderFormDownloaded || this.isUserSmart;
      });

    this.termsAndConditionsForm = this.formBuilder.group({
      tec: [false, [Validators.required]],
      clausole: [false, [Validators.required]],
      privacy: [false],
    });

    this.termsAndConditionsForm.controls.tec.disable();
    this.termsAndConditionsForm.controls.clausole.disable();
    this.termsAndConditionsForm.controls.privacy.disable();

    this.termsAndConditionService
      .hasUserAcceptedCondition()
      .subscribe((res) => {
        if (res) {
          this.navigateToUserProfile();
        } else {
          this.showPage = true;
        }
      });

    if (!this.directChannel) {
      this.orderFormDownloaded = true;
    }

    this.societyService.getSelectedSociety().subscribe(
      (res) => {
        this.societyId = res.id;
      }
    );
  }

  /**
   * Method to accept: Terms and Conditions, Privacy
   */
  acceptConditions(): void {
    if (!this.checkMandatoryConditions()) {
      return;
    }
    // SET BUTTON ACCEPT TO NOT ENABLED WHEN THE BUTTON IS PRESSED
    this.buttonAcceptEnabled = true;
    this.termsAndConditionService
      .acceptTermsAndConditions(
        this.termsAndConditionsForm.get('tec').value,
        this.termsAndConditionsForm.get('clausole').value,
        this.termsAndConditionsForm.get('privacy').value,
        this.societyId
      )
      .subscribe(
        {
          next: (res) => {
            this.authenticationUtilsService.setSessionVariable(
              'terms-conditions-accepted',
              'true',
            );
            // operating user
            if (
              this.user &&
              this.user.userType.localeCompare('OPERATING_USER') == 0
            ) {
              this.navigateToHome();
            } else {
              this.navigateToUserProfile();
            }
          },
          error: (err) => {
            this.buttonAcceptEnabled = false;
            console.log('err', err);
          },
        }
      );
  }

  navigateToUserProfile(): void {
    this.router.navigate([RoutePath.USER_PROFILE]);
  }

  navigateToHome(): void {
    this.router.navigate([RoutePath.HOME]);
  }

  /**
   *
   */
  rejectConditions(): void {
    // TODO: Add message for the user
    this.authenticationUtilsService.logout();
  }

  /**
   * Method to download General Conditions (Point&Click)
   */
  downloadGeneralConditions(): void {
    this.sharedDocumentUploadService.downloadDoc(ESharedDocumentType.GENERAL_CONDITIONS);

    this.generalConditionDownloaded = true;

    if (this.generalConditionDownloaded && this.orderFormDownloaded) {
      this.enableCheckBoxes();
    }
  }

  /**
   * Method to download General Conditions (Point&Click)
   */
  downloadOrderForm(): void {
    this.sharedDocumentUploadService.downloadDoc(this.getUserPlan())
    this.orderFormDownloaded = true;
    if (this.generalConditionDownloaded && this.orderFormDownloaded) {
      this.enableCheckBoxes();
    }
  }

  enableCheckBoxes(): void {
    this.termsAndConditionsForm.controls.tec.enable();
    if (this.termsAndConditionsForm.controls.tec.value === null) {
      this.termsAndConditionsForm.controls.tec.setValue(false);
    }
    this.termsAndConditionsForm.controls.clausole.enable();
    if (this.termsAndConditionsForm.controls.clausole.value === null) {
      this.termsAndConditionsForm.controls.clausole.setValue(false);
    }

    this.termsAndConditionsForm.controls.privacy.enable();
    if (this.termsAndConditionsForm.controls.privacy.value === null) {
      this.termsAndConditionsForm.controls.privacy.setValue(false);
    }
  }

  /**
   * Method to download Privacy (Point&Click)
   */
  downloadPrivacy(): void {

    this.sharedDocumentUploadService.downloadDoc(ESharedDocumentType.PRIVACY);
  }


  /**
   * Method to check if the user has already accepted: Terms and Conditions and/or Privacy
   */
  checkMandatoryConditions(): Boolean {
    if (
      this.termsAndConditionsForm.get('tec').value == true &&
      this.termsAndConditionsForm.get('clausole').value == true
    ) {
      this.buttonAcceptEnabled = true;
    }
    return (
      this.termsAndConditionsForm.get('tec').value &&
      this.termsAndConditionsForm.get('clausole').value
    );
  }

  getControlFormValue(control: string): Boolean {
    return this.termsAndConditionsForm.get(control).value;
  }

  getUserPlan(): ESharedDocumentType {
    if (environment.accessPlanEnabled) {
      if (this.authenticationUtilsService.getAccessPlan(EAccessPlanVariable.IS_USER_SMART))
        return ESharedDocumentType.ORDER_FORM_SMART;

      if (this.authenticationUtilsService.getAccessPlan(EAccessPlanVariable.IS_USER_PLUS))
        return ESharedDocumentType.ORDER_FORM_PLUS;

      if (this.authenticationUtilsService.getAccessPlan(EAccessPlanVariable.IS_USER_PREMIUM))
        return ESharedDocumentType.ORDER_FORM_PREMIUM;
    }

    return ESharedDocumentType.ORDER_FORM;

  }
}
